import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { usePermissions } from "@/hooks/usePermissions";
import NotificationBell from "@/components/feature/NotificationBell";

const baseNavItems = [
  { key: "nav.overview", to: "/#overview" },
  { key: "nav.products", to: "/products" },
  { key: "nav.licenses", to: "/licenses" },
  { key: "nav.status", to: "/#status" },
  { key: "nav.activity", to: "/#activity" },
];

const LANGUAGES = [
  { code: "zh", label: "简体中文" },
  { code: "en", label: "English" },
];

export default function AppHeader() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const { hasAdminAccess, can } = usePermissions();
  const navItems = hasAdminAccess
    ? [...baseNavItems, { key: "nav.admin", to: "/admin" }]
    : baseNavItems;
  const adminLinks = hasAdminAccess
    ? [
        { key: "nav.admin", to: "/admin", icon: "ri-shield-user-line", module: "licenses" as const },
        { key: "nav.adminProducts", to: "/admin/products", icon: "ri-apps-2-line", module: "products" as const },
        { key: "nav.adminTools", to: "/admin/tools", icon: "ri-tools-line", module: "licenses" as const },
        { key: "nav.adminReports", to: "/admin/reports", icon: "ri-file-chart-line", module: "reports" as const },
        { key: "nav.adminCustomers", to: "/admin/customers", icon: "ri-group-line", module: "customers" as const },
        { key: "nav.adminAudit", to: "/admin/audit", icon: "ri-file-history-line", module: "audit" as const },
        { key: "nav.adminMonitor", to: "/admin/monitor", icon: "ri-line-chart-line", module: "monitor" as const },
        { key: "nav.adminStaff", to: "/admin/staff", icon: "ri-shield-star-line", module: "staff" as const },
        { key: "nav.adminApiKeys", to: "/admin/apikeys", icon: "ri-plug-2-line", module: "apikeys" as const },
        { key: "nav.adminApprovals", to: "/admin/approvals", icon: "ri-flow-chart", module: "approvals" as const },
        { key: "nav.adminHelp", to: "/admin/help", icon: "ri-questionnaire-line", module: "help" as const },
      ].filter((item) => can(item.module))
    : [];

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setUser(data.session?.user ?? null);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const displayName = user
    ? (user.user_metadata?.full_name as string | undefined) || user.email?.split("@")[0] || t("nav.settings")
    : "";
  const initials = displayName.slice(0, 1).toUpperCase();
  const currentLang = i18n.language?.startsWith("en") ? "en" : "zh";

  const handleSignOut = async () => {
    setMenuOpen(false);
    await supabase.auth.signOut();
    setUser(null);
  };

  const switchLanguage = async (code: string) => {
    await i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const languageSwitcher = (
    <div className="relative" ref={langRef}>
      <button
        type="button"
        onClick={() => setLangOpen((value) => !value)}
        className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-200 bg-background-100 px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300"
        aria-label={t("nav.language")}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-translate-2" />
        </span>
        {currentLang === "en" ? "EN" : "中"}
        <i className="ri-arrow-down-s-line text-base text-foreground-400" />
      </button>
      {langOpen && (
        <div className="absolute right-0 mt-2 w-36 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => void switchLanguage(lang.code)}
              className={`flex w-full cursor-pointer items-center justify-between px-4 py-2 text-sm transition-colors hover:bg-background-100 ${
                currentLang === lang.code ? "text-primary-700" : "text-foreground-700"
              }`}
            >
              {lang.label}
              {currentLang === lang.code && (
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-check-line" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2" aria-label={t("nav.brandHome")}>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-shield-keyhole-line text-lg" />
          </span>
          <span className="font-heading text-lg font-semibold tracking-tight text-foreground-950">
            LicenseHub
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="cursor-pointer rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              {t(item.key)}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          {languageSwitcher}
          {user ? (
            <>
              <Link
                to="/apply"
                className="whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
              >
                {t("nav.quickApply")}
              </Link>
              <NotificationBell />
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((value) => !value)}
                  className="flex cursor-pointer items-center gap-2 rounded-full border border-background-200 bg-background-100 py-1 pl-1 pr-2 transition-colors hover:border-primary-300"
                  aria-label={t("nav.userMenu")}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-background-50">
                    {initials}
                  </span>
                  <i className="ri-arrow-down-s-line text-base text-foreground-500" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-60 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                    <div className="border-b border-background-200 px-4 py-3">
                      <p className="truncate text-sm font-medium text-foreground-950">{displayName}</p>
                      <p className="truncate text-xs text-foreground-500">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/licenses"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-key-2-line" />
                        </span>
                        {t("nav.licenses")}
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-user-settings-line" />
                        </span>
                        {t("nav.settings")}
                      </Link>
                      {adminLinks.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className={item.icon} />
                          </span>
                          {t(item.key)}
                        </Link>
                      ))}
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-sm text-accent-700 transition-colors hover:bg-accent-50"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-logout-box-r-line" />
                        </span>
                        {t("nav.signOut")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/apply"
                className="whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
              >
                {t("nav.quickApply")}
              </Link>
              <Link
                to="/auth"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("nav.login")}
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((value) => !value)}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
          aria-label={t("nav.openMenu")}
        >
          <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="cursor-pointer rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>
          <div className="mt-3 flex items-center gap-2 border-t border-background-200 pt-3">
            <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
              <i className="ri-translate-2" />
            </span>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  void switchLanguage(lang.code);
                  setMobileOpen(false);
                }}
                className={`cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  currentLang === lang.code
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700"
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-2 border-t border-background-200 pt-3">
            {user ? (
              <>
                <Link
                  to="/apply"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md border border-background-200 px-4 py-2 text-center text-sm text-foreground-800"
                >
                  {t("nav.quickApply")}
                </Link>
                <Link
                  to="/licenses"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md border border-background-200 px-4 py-2 text-center text-sm text-foreground-800"
                >
                  {t("nav.licenses")}
                </Link>
                <Link
                  to="/notifications"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md border border-background-200 px-4 py-2 text-center text-sm text-foreground-800"
                >
                  {t("nav.notifications")}
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md border border-background-200 px-4 py-2 text-center text-sm text-foreground-800"
                >
                  {t("nav.settings")}
                </Link>
                {adminLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-md border border-background-200 px-4 py-2 text-center text-sm text-foreground-800"
                  >
                    {t(item.key)}
                  </Link>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    void handleSignOut();
                  }}
                  className="cursor-pointer rounded-md border border-accent-300 bg-accent-50 px-4 py-2 text-center text-sm text-accent-700"
                >
                  {t("nav.signOut")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/apply"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md border border-background-200 px-4 py-2 text-center text-sm text-foreground-800"
                >
                  {t("nav.quickApply")}
                </Link>
                <Link
                  to="/auth"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md bg-primary-500 px-4 py-2 text-center text-sm font-medium text-background-50"
                >
                  {t("nav.login")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}